'use client';

import {
  type FC,
  useId,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { type HighlightBoxProps } from './HighlightBox.types';
import styles from './HighlightBox.module.css';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStyles,
  useDismiss,
} from '@floating-ui/react';
import { Button, ButtonGroup, Flex } from '@/_components';
import { OnboardingContext } from '@/_contexts/OnboardingProvider';

export const HighlightBox: FC<HighlightBoxProps> = ({
  children,
  id,
  set,
  content,
  position = 'top',
  isDisabled = false,
  hideContentByDefault = false,
}) => {
  const {
    dismissHighlight,
    onNextHighlight,
    onPreviousHighlight,
    getHasNextHighlight,
    getHasPreviousHighlight,
    registerHighlight,
    unregisterHighlight,
    isHighlightActive,
    isHighlightDismissed,
    loadHighlightDismissState,
    isInitialized,
  } = useContext(OnboardingContext);

  // Effect for set-based highlights
  useEffect(() => {
    if (set) {
      registerHighlight(id, set);
      setHasRegistered(true);
      return () => unregisterHighlight(id, set);
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, set]);

  // Effect for non-set highlights
  useEffect(() => {
    if (!set) {
      loadHighlightDismissState(id);
      setHasRegistered(true);
    }
  }, [id, set, loadHighlightDismissState]);

  // Check if this highlight is active in its set
  const isActiveInSet = isHighlightActive(id, set);

  // Check if this highlight has been dismissed
  const isDismissed = isHighlightDismissed(id);

  // For highlights in sets, we need to wait for registration to complete
  // before we can determine if they should show
  const [hasRegistered, setHasRegistered] = useState(false);

  const [isClickToggled, setIsClickToggled] = useState(!hideContentByDefault); // Start visible unless hidden by default
  const contentId = useId();
  const triggerId = useId();

  const hasNextHighlight = getHasNextHighlight(set);
  const hasPreviousHighlight = getHasPreviousHighlight(set);

  const { refs, floatingStyles, context } = useFloating({
    placement: position,
    open: true, // Always open for positioning
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: position.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
  ]);

  const { styles: transitionStyles } = useTransitionStyles(context, {
    duration: 96,
  });

  const handleToggleContent = useCallback(
    (e: React.MouseEvent) => {
      if (isDisabled) return;

      e.stopPropagation();
      e.preventDefault();

      // Always toggle the visibility state when dot or close button is clicked
      setIsClickToggled(!isClickToggled);
    },
    [isDisabled, isClickToggled],
  );

  // Determine if content should show
  const shouldShowContent = useMemo(() => {
    if (isDisabled || isDismissed) return false;
    // If part of a set, only show if active in that set
    if (set && !isActiveInSet) return false;
    return isClickToggled;
  }, [isDisabled, isDismissed, set, isActiveInSet, isClickToggled]);

  const onDismiss = () => dismissHighlight(id);

  const handleNextHighlight = useCallback(() => {
    onNextHighlight(id, set);
  }, [id, set, onNextHighlight]);

  const handlePreviousHighlight = useCallback(() => {
    onPreviousHighlight(id, set);
  }, [id, set, onPreviousHighlight]);

  return (
    <>
      <div
        ref={refs.setReference}
        className={styles.highlightBoxTrigger}
        {...getReferenceProps({
          id: triggerId,
          'aria-describedby': shouldShowContent ? contentId : undefined,
        })}
      >
        {children}
        {!isDisabled &&
          !isDismissed &&
          isInitialized &&
          hasRegistered &&
          (!set || isActiveInSet) && (
            <div
              className={styles.pulsatingDot}
              onClick={handleToggleContent}
              role="button"
              aria-label="Toggle tooltip visibility"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggleContent(e as any);
                }
              }}
            />
          )}
      </div>
      {shouldShowContent && (
        <FloatingPortal>
          <Flex
            ref={refs.setFloating}
            id={contentId}
            isColumn={true}
            className={styles.highlightBoxContent}
            style={{
              ...floatingStyles,
              ...transitionStyles,
            }}
            gap={8}
            padding={8}
            {...getFloatingProps()}
          >
            <ButtonGroup
              align="right"
              className={styles.highlightBox__closeButton}
            >
              <Button
                intent="ghost"
                value="Close"
                iconLeft="x"
                size="small"
                isIconOnly={true}
                onClick={onDismiss}
              />
            </ButtonGroup>
            {content}
            {set && (
              <ButtonGroup
                align="right"
                className={styles.highlightBox__navigation}
              >
                <Button
                  intent="secondary"
                  value="Previous"
                  iconLeft="caret-left"
                  size="small"
                  isDisabled={!hasPreviousHighlight}
                  onClick={handlePreviousHighlight}
                />
                <Button
                  intent="secondary"
                  value="Next"
                  iconRight="caret-right"
                  isDisabled={!hasNextHighlight}
                  size="small"
                  onClick={handleNextHighlight}
                />
              </ButtonGroup>
            )}
          </Flex>
        </FloatingPortal>
      )}
    </>
  );
};
